import type { ReactiveVar } from "@apollo/client";

import {
  AnalyticsEvent as AnalyticsEventType,
  AnalyticsEvent,
} from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import type { FilterSource, FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { SearchType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { makeFSVar } from "@shared/reactiveVar/makeFSVar";
type SearchAnalytics = {
  eventName: AnalyticsEventType | undefined;
  entryPoint: SearchApplyEntryPoint | undefined;
  type: SearchType | undefined;
  filterType: FilterType | undefined;
  content: string | undefined;
  recentSearchRank: number | undefined;
  resultCount: number | undefined;
  filterSource?: FilterSource | undefined;
};

const INITIAL_STATE: SearchAnalytics = {
  eventName: undefined,
  entryPoint: undefined,
  type: undefined,
  filterType: undefined,
  content: undefined,
  recentSearchRank: undefined,
  resultCount: undefined,
  filterSource: undefined,
};

const searchAnalyticsVar: ReactiveVar<SearchAnalytics> = makeFSVar(
  INITIAL_STATE,
  "useSearchAnalyticsVar"
);

const setSearchAnalyticToMegaMenu = (content: string, type: SearchType) => {
  searchAnalyticsVar({
    ...searchAnalyticsVar(),
    eventName: AnalyticsEvent.STORE_SEARCH_APPLY,
    type,
    content,
    recentSearchRank: undefined,
    filterType: undefined,
    filterSource: undefined,
  });
};

const setSearchAnalyticToQuery = (
  eventName: AnalyticsEvent,
  entryPoint: SearchApplyEntryPoint,
  content: string,
  recentSearchRank?: number
) => {
  searchAnalyticsVar({
    ...searchAnalyticsVar(),
    eventName,
    entryPoint,
    type: SearchType.QUERY,
    content,
    recentSearchRank: recentSearchRank ? recentSearchRank : undefined,
    filterType: undefined,
    filterSource: undefined,
  });
};

const setSearchAnalyticToTypeahead = (
  eventName: AnalyticsEvent,
  entryPoint: SearchApplyEntryPoint,
  content: string,
  type: SearchType,
  recentSearchRank?: number
) => {
  searchAnalyticsVar({
    ...searchAnalyticsVar(),
    eventName,
    entryPoint,
    content,
    type,
    recentSearchRank: recentSearchRank ? recentSearchRank : undefined,
    filterType: undefined,
    filterSource: undefined,
  });
};

const setSearchAnalyticEntryPoint = (entryPoint: SearchApplyEntryPoint) => {
  searchAnalyticsVar({
    ...searchAnalyticsVar(),
    entryPoint,
  });
};

const setSearchAnalyticToFilter = (
  eventName: AnalyticsEvent,
  entryPoint: SearchApplyEntryPoint,
  content: string,
  filterType: FilterType,
  filterSource?: FilterSource
) => {
  searchAnalyticsVar({
    ...searchAnalyticsVar(),
    eventName,
    entryPoint,
    content,
    filterType,
    type: undefined,
    recentSearchRank: undefined,
    ...getFilterSourcePayload(eventName, filterSource),
  });
};

const getFilterSourcePayload = (eventName: AnalyticsEvent, filterSource?: FilterSource) => {
  return (
    (eventName === AnalyticsEventType.STORE_SEARCH_APPLY_FILTER ||
      eventName === AnalyticsEventType.PATIENT_SEARCH_APPLY_FILTER) &&
    filterSource && {
      filterSource,
    }
  );
};

const setSearchAnalyticResultCount = (resultCount: number) => {
  searchAnalyticsVar({
    ...searchAnalyticsVar(),
    resultCount,
  });
};

const shouldAddResultCount = () => {
  const eventName = searchAnalyticsVar().eventName;

  return (
    eventName === AnalyticsEvent.STORE_SEARCH_APPLY_FILTER ||
    eventName === AnalyticsEvent.PATIENT_SEARCH_APPLY_FILTER
  );
};

export {
  searchAnalyticsVar,
  setSearchAnalyticToMegaMenu,
  setSearchAnalyticToQuery,
  setSearchAnalyticToTypeahead,
  setSearchAnalyticEntryPoint,
  setSearchAnalyticToFilter,
  setSearchAnalyticResultCount,
  shouldAddResultCount,
};
